/**
 * OR-6486: one condensed card for a run of concept-group drafts, in place of
 * the stack of full {@link AssetCard}s a workbook upload would otherwise push
 * into the chat. The "Artifact List Card" of the object-cards spec (Figma node
 * 6101-25282).
 *
 * Same chassis as the other chat object cards (white card, 3px brand-primary
 * left rail, kind icon + kind label header), but the header carries the run's
 * size and each draft is one row — name, status chip, `View details ›`. Rows
 * past the sixth sit behind a "Show N more" toggle, so a 60-sheet workbook
 * costs the transcript the same vertical space as a 6-sheet one.
 *
 * No per-row timestamp: the rows are one tool call's output, created seconds
 * apart, so the times carry nothing the researcher can act on. Each row's
 * status is its own query, and only mounted rows query — a collapsed card of
 * 60 drafts issues six requests, not sixty.
 *
 * Rows run 33px against the spec's 35: the shared {@link ArtifactStatusChip} is
 * MUI's 24px small chip where the mock pins a 20px one. Unforked on purpose —
 * every artifact surface reads the same chip height.
 */

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, ButtonBase, Divider, Stack, Typography } from '@mui/material';
import { Fragment, useState } from 'react';
import { useIntl } from 'react-intl';

import { ArtifactStatusChip } from '@verantos-dev/shared-components';
import { borderRadius, shadows } from '@verantos-dev/shared-ui-components/tokens';
import { ASSET_STATUS } from '@verantos-dev/types-projects';

import { useConceptGroupStatusLabel } from '../hooks/useConceptGroupStatusLabel';
import type { EpiResourceRef } from '../types/epi-chat-types';
import { resourceCardRoute } from '../utils/resourceCardRoute';

/** Runs longer than this condense; three or fewer keep their full asset cards. */
export const CONCEPT_GROUP_CONDENSE_THRESHOLD = 3;

/** Rows visible before the "Show N more" toggle. */
export const CONCEPT_GROUP_COLLAPSED_ROWS = 6;

interface ConceptGroupBatchRowProps {
  resourceRef: EpiResourceRef;
  onNavigateResource?: (route: string) => void;
  /** See `useConceptGroupStatusLabel`'s `defer` param (OR-6858). */
  deferStatusFetch?: boolean;
}

const ConceptGroupBatchRow = ({
  resourceRef,
  onNavigateResource,
  deferStatusFetch,
}: ConceptGroupBatchRowProps) => {
  const intl = useIntl();
  const statusLabel = useConceptGroupStatusLabel(resourceRef, deferStatusFetch);
  const isDisabled = resourceRef.status === 'error' || !onNavigateResource;

  return (
    <ButtonBase
      onClick={() => {
        if (isDisabled) return;
        onNavigateResource?.(resourceCardRoute(resourceRef));
      }}
      disabled={isDisabled}
      data-testid={`concept-group-batch-row-${resourceRef.resourceId ?? resourceRef.clientId}`}
      aria-label={`Open ${resourceRef.name}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        width: '100%',
        paddingY: 1,
        textAlign: 'left',
        opacity: isDisabled ? 0.72 : 1,
        '&:focus-visible': {
          outline: (theme) => `2px solid ${theme.palette.primary.main}`,
          outlineOffset: -2,
        },
      }}
    >
      <Typography
        variant="body2"
        sx={{ flex: 1, minWidth: 0, fontWeight: 500, color: 'text.primary' }}
        noWrap
      >
        {resourceRef.name}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={4} sx={{ flexShrink: 0 }}>
        <ArtifactStatusChip
          status={resourceRef.status === 'error' ? ASSET_STATUS.ERROR : statusLabel}
          data-testid={`concept-group-batch-row-status-chip-${
            resourceRef.resourceId ?? resourceRef.clientId
          }`}
        />
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ color: isDisabled ? 'text.secondary' : 'primary.main' }}
        >
          {/* The theme's body2 variant sets its own color, so inheriting the
              Stack's primary is not enough — pin it on the element. */}
          <Typography
            variant="body2"
            sx={{ fontWeight: (theme) => theme.typography.fontWeightMedium, color: 'inherit' }}
          >
            {intl.formatMessage({
              id: 'epiChat.assetCard.viewDetails',
              defaultMessage: 'View details',
            })}
          </Typography>
          <ChevronRightIcon sx={{ fontSize: 18 }} aria-hidden />
        </Stack>
      </Stack>
    </ButtonBase>
  );
};

export interface ConceptGroupBatchCardProps {
  /** The run's drafts, in the order they arrived. */
  resourceRefs: EpiResourceRef[];
  onNavigateResource?: (route: string) => void;
  /** See `useConceptGroupStatusLabel`'s `defer` param (OR-6858). */
  deferStatusFetch?: boolean;
}

export const ConceptGroupBatchCard = ({
  resourceRefs,
  onNavigateResource,
  deferStatusFetch,
}: ConceptGroupBatchCardProps) => {
  const intl = useIntl();
  const [expanded, setExpanded] = useState(false);
  const hiddenCount = resourceRefs.length - CONCEPT_GROUP_COLLAPSED_ROWS;
  const visibleRefs = expanded ? resourceRefs : resourceRefs.slice(0, CONCEPT_GROUP_COLLAPSED_ROWS);

  return (
    <Box sx={{ marginY: 2.5, marginX: { xs: 0, sm: 2 } }}>
      <Box
        data-testid="concept-group-batch-card"
        sx={{
          padding: 4,
          backgroundColor: 'background.paper',
          borderLeft: (theme) => `3px solid ${theme.palette.primary.main}`,
          borderRadius: `${borderRadius.sm}px`,
          boxShadow: shadows.sm,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={4} sx={{ paddingBottom: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
            <DataObjectIcon
              sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }}
              aria-hidden
            />
            <Typography variant="body2" color="text.secondary" noWrap>
              {intl.formatMessage({
                id: 'epiChat.conceptGroupBatch.kindLabel',
                defaultMessage: 'Concept group',
              })}
            </Typography>
          </Stack>
          <Typography
            variant="body2"
            data-testid="concept-group-batch-count"
            sx={{
              color: 'text.primary',
              fontWeight: (theme) => theme.typography.fontWeightMedium,
            }}
            noWrap
          >
            {intl.formatMessage(
              {
                id: 'epiChat.conceptGroupBatch.count',
                defaultMessage: '{count, plural, one {# group} other {# groups}}',
              },
              { count: resourceRefs.length }
            )}
          </Typography>
        </Stack>
        <Divider />
        {visibleRefs.map((resourceRef) => (
          <Fragment key={resourceRef.resourceId ?? resourceRef.clientId ?? resourceRef.name}>
            <ConceptGroupBatchRow
              resourceRef={resourceRef}
              onNavigateResource={onNavigateResource}
              deferStatusFetch={deferStatusFetch}
            />
            <Divider />
          </Fragment>
        ))}
        {hiddenCount > 0 && (
          <Button
            variant="text"
            color="primary"
            size="small"
            data-testid="concept-group-batch-toggle"
            onClick={() => setExpanded((wasExpanded) => !wasExpanded)}
            endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ marginTop: 4, marginLeft: -1, textTransform: 'none' }}
          >
            {expanded
              ? intl.formatMessage({
                  id: 'epiChat.conceptGroupBatch.showLess',
                  defaultMessage: 'Show less',
                })
              : intl.formatMessage(
                  {
                    id: 'epiChat.conceptGroupBatch.showMore',
                    defaultMessage: 'Show {count} more',
                  },
                  { count: hiddenCount }
                )}
          </Button>
        )}
      </Box>
    </Box>
  );
};
